import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, useAuth } from "../App";
import useSmartBack from "../hooks/useSmartBack";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  CalendarIcon,
  MapPin,
  Shield,
  Truck,
  CreditCard,
  Loader2,
  ChevronLeft,
  Check,
  AlertCircle,
  HelpCircle,
  Headphones,
  ShieldCheck,
  ReceiptText,
  Lock,
  Clock,
  Sparkles,
  CreditCard as CardIcon,
} from "lucide-react";
import PremiumIcon from "../components/ui/PremiumIcon";
import { format, addDays } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { toast } from "sonner";
import UsdHint from "../components/UsdHint";

export default function CheckoutPage() {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const goBack = useSmartBack("/search");
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const { user } = useAuth();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Booking form state — pre-fill from query params if present (from CarDetailsPage TripScheduler)
  const initialPickup = (() => {
    const p = searchParams.get("pickup");
    if (p) {
      const d = new Date(p);
      if (!isNaN(d)) return d;
    }
    return addDays(new Date(), 1);
  })();
  const initialReturn = (() => {
    const r = searchParams.get("return");
    if (r) {
      const d = new Date(r);
      if (!isNaN(d)) return d;
    }
    return addDays(new Date(), 3);
  })();

  const [pickupDate, setPickupDate] = useState(initialPickup);
  const [returnDate, setReturnDate] = useState(initialReturn);
  const [pickupType, setPickupType] = useState("host_address");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [protectionPlan, setProtectionPlan] = useState("standard");
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [whyMinFeeOpen, setWhyMinFeeOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [premiumEligibility, setPremiumEligibility] = useState(null);
  const [vipCheckoutLoading, setVipCheckoutLoading] = useState(false);
  const [vipReferralCode, setVipReferralCode] = useState("");
  const [vipReferralStatus, setVipReferralStatus] = useState(null); // {valid, reason, bonus_days, referrer_name}
  const [vipReferralChecking, setVipReferralChecking] = useState(false);

  // Fetch Premium eligibility on mount + after Stripe-VIP redirect
  useEffect(() => {
    let cancelled = false;
    const fetchEligibility = async () => {
      try {
        const { data } = await api.get("/users/me/premium-eligibility");
        if (!cancelled) setPremiumEligibility(data);
      } catch (_e) {
        if (!cancelled)
          setPremiumEligibility({
            eligible: false,
            via: null,
            completed_rental_count: 0,
            vip_active: false,
            reason: "No se pudo verificar la elegibilidad",
            required_rentals: 10,
            vip_price_mxn: 3500,
          });
      }
    };
    fetchEligibility();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleActivateVip = async () => {
    setVipCheckoutLoading(true);
    try {
      const body = vipReferralCode.trim()
        ? { referral_code: vipReferralCode.trim().toUpperCase() }
        : {};
      const { data } = await api.post("/users/me/vip/checkout", body);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.vipStartFailed"));
      setVipCheckoutLoading(false);
    }
  };

  // Debounced referral-code validator
  useEffect(() => {
    const code = vipReferralCode.trim().toUpperCase();
    if (!code) {
      setVipReferralStatus(null);
      return;
    }
    if (code.length < 6) {
      // RVNS-XXXX is 9 chars; require at least the dash + 1 char
      setVipReferralStatus(null);
      return;
    }
    setVipReferralChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/users/me/vip/referral/validate", { referral_code: code });
        setVipReferralStatus(data);
      } catch {
        setVipReferralStatus({ valid: false, reason: "error" });
      } finally {
        setVipReferralChecking(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [vipReferralCode]);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  // Optional abandoned-checkout recovery code (from the "Tu reserva te espera"
  // email). Forwarded to /api/payments/checkout where the backend validates
  // and applies the discount. Pre-filled from `?code=` query param so the
  // email CTA can land guests with the code already applied.
  const [recoveryCode, setRecoveryCode] = useState(
    (searchParams.get("code") || "").toUpperCase(),
  );

  // Protection plans must match the backend `FeeSettings.protection_plans`.
  // Final charge is `max(minFee, base_price * feePercent/100)`. The
  // `deductible` is a REFUNDABLE security hold — never charged today; only
  // surfaced so the renter knows what they could be liable for in case of
  // damage. The backend `calculate_booking_price` returns the authoritative
  // numbers we display in the summary; these constants are only used for the
  // plan-selector copy.
  const protectionPlans = [
    { id: "premier", icon: Sparkles, tone: "amber", feePercent: 20, minFee: 500, deductible: 0 },
    { id: "standard", icon: ShieldCheck, tone: "blue", feePercent: 15, minFee: 350, deductible: 10000 },
    { id: "minimum", icon: Shield, tone: "neutral", feePercent: 10, minFee: 250, deductible: 20000 },
  ].map((p) => ({
    ...p,
    name: t(`checkout.plans.${p.id}.name`),
    tagline: t(`checkout.plans.${p.id}.tagline`),
    description: t(`checkout.plans.${p.id}.description`),
    benefits: t(`checkout.plans.${p.id}.benefits`, { returnObjects: true }),
    rule: t(`checkout.plans.${p.id}.rule`),
  }));

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await api.get(`/vehicles/${vehicleId}`);
        setVehicle(response.data);
      } catch (error) {
        console.error("Error fetching vehicle:", error);
        toast.error(t("checkout.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [vehicleId]);

  // Check for payment return
  useEffect(() => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (payment === "canceled") {
      toast.error(t("checkout.paymentCanceled"));
    }
  }, [searchParams]);

  // Calculate price when dates or options change
  useEffect(() => {
    if (vehicle && pickupDate && returnDate) {
      calculatePrice();
    }
  }, [vehicle, pickupDate, returnDate, pickupType, protectionPlan]);

  const calculatePrice = async () => {
    if (!vehicle) return;

    setCalculatingPrice(true);
    try {
      const response = await api.post("/bookings/calculate", {
        vehicle_id: vehicleId,
        pickup_date: pickupDate.toISOString(),
        return_date: returnDate.toISOString(),
        pickup_type: pickupType,
        protection_plan: protectionPlan,
      });
      setPriceBreakdown(response.data);
    } catch (error) {
      console.error("Error calculating price:", error);
    } finally {
      setCalculatingPrice(false);
    }
  };

  const isAdmin = user?.role === "admin";
  // Pre-flight gate: if the host hasn't completed their Stripe Connect
  // account yet, every checkout attempt will 503. Surface that up-front so
  // the guest never clicks "Pagar" only to bounce. Admins bypass (same as
  // the backend) so support can run live test bookings.
  const checkoutGateBlocked = !isAdmin && vehicle && vehicle.payments_ready === false;

  const handleSubmit = async () => {
    if (!pickupDate || !returnDate) {
      toast.error(t("checkout.errSelectDates"));
      return;
    }

    if (pickupType === "delivery" && !deliveryAddress.trim()) {
      toast.error(t("checkout.errDeliveryAddress"));
      return;
    }

    if (!acceptedTerms) {
      toast.error(t("checkout.errAcceptTerms"));
      return;
    }

    // Pre-flight host-Connect gate — surface BEFORE we even try to create
    // the booking, so a misconfigured listing never strands a guest.
    if (checkoutGateBlocked) {
      toast.error(t("checkout.hostNotReady"));
      return;
    }

    // Check driver verification — mirror the backend gate at server.py L3084:
    // admin role bypasses; otherwise either `identity_verified` (Stripe Identity)
    // OR `verification_status === "approved"` (legacy manual review) is enough.
    const identityOk = !!user?.identity_verified;
    const legacyOk = user?.verification_status === "approved";
    if (!isAdmin && !identityOk && !legacyOk) {
      toast.error(t("checkout.errVerification"));
      navigate("/profile");
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: create the booking — it starts as `pending_payment` on the
      // backend; the status only flips to `confirmed`/`requested` AFTER the
      // Stripe webhook confirms payment.
      let bookingResponse;
      try {
        bookingResponse = await api.post("/bookings", {
          vehicle_id: vehicleId,
          pickup_date: pickupDate.toISOString(),
          return_date: returnDate.toISOString(),
          pickup_type: pickupType,
          delivery_address: pickupType === "delivery" ? deliveryAddress : null,
          protection_plan: protectionPlan,
        });
      } catch (err) {
        // Surface the most actionable message we can pull from the error.
        // Order of preference: backend `detail` → backend `message` → raw
        // string body (some 4xx responses on iOS Safari arrive as text) →
        // axios error.message → generic fallback. The status code is
        // ALWAYS shown so support can tell verified-but-blocked (403) apart
        // from a real server failure (500) from a network glitch (no
        // status).
        const status = err?.response?.status;
        let detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          err?.message;
        if (Array.isArray(detail)) detail = detail.map((x) => x.msg || x).join(", ");
        // Verification gate is by far the most common 403 here — surface a
        // direct call-to-action instead of the raw backend string so the
        // guest knows what to do next.
        const isVerificationBlock =
          status === 403 && /verifica/i.test(String(detail || ""));
        // Host's Stripe Connect not active — surfaced as 503 with the exact
        // Spanish copy from the spec. Show as a clear, non-technical toast.
        const isHostNotReady =
          status === 503 && /cuenta de pagos/i.test(String(detail || ""));
        console.error(
          "[booking create] failed",
          { status, detail, data: err?.response?.data, headers: err?.response?.headers },
        );
        if (isVerificationBlock) {
          toast.error(t("checkout.errIdentity"), {
            description: t("checkout.errIdentityDesc"),
            duration: 8000,
          });
        } else if (isHostNotReady) {
          toast.error(t("checkout.hostNotReady"), { duration: 8000 });
        } else {
          toast.error(
            detail
              ? t("checkout.errCreateWithDetail", { status: status || "—", detail })
              : t("checkout.errCreateGeneric", { status: status ? ` (${status})` : "" }),
          );
        }
        setSubmitting(false);
        return;
      }

      const bookingId = bookingResponse.data.booking_id;

      // Step 2: create the Stripe Checkout session. If this fails, the booking
      // stays at `pending_payment` (it'll be auto-cleaned by the 30-min window
      // in availability checks) so the vehicle isn't blocked.
      let checkoutResponse;
      try {
        checkoutResponse = await api.post("/payments/checkout", {
          booking_id: bookingId,
          origin_url: window.location.origin,
          recovery_code: recoveryCode || undefined,
        });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        console.error("[stripe checkout] failed:", err?.response?.status, detail || err?.message, "booking_id=", bookingId);
        toast.error(
          detail
            ? t("checkout.errPayWithDetail", { detail })
            : t("checkout.errPayGeneric"),
        );
        setSubmitting(false);
        return;
      }

      // Redirect to Stripe — they'll come back to /trips/{id}?payment=success
      // (or /checkout/{id}?payment=canceled) and our success-page poll +
      // webhook will then finalize the booking.
      if (!checkoutResponse?.data?.url) {
        console.error("[stripe checkout] missing url in response", checkoutResponse?.data);
        toast.error(t("checkout.errPayGeneric"));
        setSubmitting(false);
        return;
      }
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      // Catch-all for unexpected errors not handled above.
      console.error("[checkout] unexpected error:", error?.response?.data || error);
      toast.error(t("checkout.errProcess"));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("checkout.notFound")}</h2>
        <Button onClick={() => navigate("/search")} className="btn-primary">
          {t("carDetails.searchCars")}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand pb-8" data-testid="checkout-page">
      <div className="container-main py-6">
        {/* Back Button */}
        <button
          onClick={goBack}
          className="inline-flex items-center text-gray-600 hover:text-terracotta mb-6"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t("backNav.back")}
        </button>

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-8">
          {t("checkout.title")}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vehicle Summary */}
            <div className="card-base">
              <div className="flex gap-4">
                <img
                  src={vehicle.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=200"}
                  alt={`${vehicle.make} ${vehicle.model}`}
                  className="w-24 h-24 object-cover rounded-xl"
                />
                <div>
                  <h3 className="font-heading font-semibold text-lg">
                    {vehicle.make} {vehicle.model} {vehicle.year}
                  </h3>
                  <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {vehicle.location_city}
                  </p>
                  <p className="text-terracotta font-semibold mt-2">
                    ${vehicle.daily_rate?.toLocaleString()} {t("carCard.perDay")}{" "}
                    <UsdHint amountMxn={vehicle.daily_rate} />
                  </p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-terracotta" />
                {t("checkout.tripDates")}
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("checkout.pickupDateLabel")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal mt-2 h-12"
                        data-testid="checkout-pickup-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {pickupDate ? format(pickupDate, "dd MMM yyyy", { locale: dateLocale }) : t("checkout.select")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>{t("checkout.returnDateLabel")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal mt-2 h-12"
                        data-testid="checkout-return-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {returnDate ? format(returnDate, "dd MMM yyyy", { locale: dateLocale }) : t("checkout.select")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={returnDate}
                        onSelect={setReturnDate}
                        disabled={(date) => date <= pickupDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Pickup Type */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-terracotta" />
                {t("checkout.pickupTypeTitle")}
              </h3>

              <RadioGroup value={pickupType} onValueChange={setPickupType}>
                <div className="space-y-3">
                  <div
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      pickupType === "host_address"
                        ? "border-terracotta bg-terracotta/5"
                        : "border-[#E5E0D8]"
                    }`}
                    onClick={() => setPickupType("host_address")}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="host_address" id="host_address" />
                      <div>
                        <Label htmlFor="host_address" className="font-medium cursor-pointer">
                          {t("checkout.pickupHost")}
                        </Label>
                        <p className="text-sm text-gray-500 mt-1">
                          {vehicle.location_address}
                        </p>
                      </div>
                    </div>
                  </div>

                  {vehicle.delivery_enabled && (
                    <div
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        pickupType === "delivery"
                          ? "border-terracotta bg-terracotta/5"
                          : "border-[#E5E0D8]"
                      }`}
                      onClick={() => setPickupType("delivery")}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="delivery" id="delivery" />
                        <div className="flex-1">
                          <Label htmlFor="delivery" className="font-medium cursor-pointer">
                            {t("checkout.delivery")}
                          </Label>
                          <p className="text-sm text-gray-500 mt-1">
                            {t("checkout.deliveryFee", { fee: vehicle.delivery_fee, km: vehicle.delivery_radius_km })}
                          </p>
                        </div>
                      </div>

                      {pickupType === "delivery" && (
                        <div className="mt-4">
                          <Input
                            placeholder={t("checkout.deliveryAddressPlaceholder")}
                            value={deliveryAddress}
                            onChange={(e) => setDeliveryAddress(e.target.value)}
                            className="input-base"
                            data-testid="delivery-address-input"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </RadioGroup>
            </div>

            {/* Protection Plan */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-3">
                <PremiumIcon icon={Shield} variant="gradient" tone="terracotta" size="sm" />
                {t("checkout.protectionPlanTitle")}
                <button
                  type="button"
                  onClick={() => setWhyMinFeeOpen(true)}
                  className="ml-1 text-neutral-400 hover:text-terracotta transition-colors inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                  aria-label={t("booking.whyMinFee.cta", "¿Por qué hay una tarifa mínima?")}
                  data-testid="why-min-fee-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </h3>

              <div className="space-y-3">
                {protectionPlans.map((plan) => {
                  // Live "this trip would cost" preview per plan so guests can compare
                  // and self-upsell. We compute against `priceBreakdown.base_price`
                  // (the only authoritative number on this page).
                  const base = priceBreakdown?.base_price || 0;
                  const pctAmount = Math.round((base * plan.feePercent) / 100);
                  const thisPlanFee = base ? Math.max(plan.minFee, pctAmount) : null;
                  const isPremium = plan.id === "premier";
                  const isLocked =
                    isPremium && premiumEligibility && !premiumEligibility.eligible;
                  const isSelected = protectionPlan === plan.id;

                  return (
                  <div
                    key={plan.id}
                    className={`plan-card relative p-4 rounded-xl ${
                      isLocked
                        ? "opacity-90 cursor-default border-dashed border-2 border-amber-300 bg-amber-50/30"
                        : "cursor-pointer"
                    } ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      if (isLocked) return;
                      setProtectionPlan(plan.id);
                    }}
                    data-testid={`protection-plan-${plan.id}`}
                  >
                    {/* Header row: radio + name + price (price stacks below on mobile) */}
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                          isLocked
                            ? "border-amber-400 bg-amber-100"
                            : isSelected
                            ? "border-terracotta bg-terracotta"
                            : "border-gray-300"
                        }`}
                      >
                        {isLocked ? (
                          <Lock className="w-3 h-3 text-amber-600" />
                        ) : isSelected ? (
                          <Check className="w-3 h-3 text-white" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Row 1: name + tagline */}
                        <p className="font-semibold flex items-center gap-2 flex-wrap">
                          {plan.icon && (
                            <PremiumIcon
                              icon={plan.icon}
                              variant={isSelected ? "gradient" : "glass"}
                              tone={plan.tone || "neutral"}
                              size="xs"
                              animate={false}
                            />
                          )}
                          {plan.name}
                          {isPremium && premiumEligibility?.via === "vip_membership" && !isLocked && (
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full">VIP</span>
                          )}
                        </p>
                        <p className="text-xs text-terracotta font-medium mt-0.5">
                          {plan.tagline}
                        </p>
                        {/* Row 2 (mobile-first): price + rule. Full width on phone,
                            absolute-positioned top-right on desktop (via sm: classes). */}
                        <div className="mt-2 sm:absolute sm:top-4 sm:right-4 sm:text-right text-left">
                          {thisPlanFee != null ? (
                            <>
                              <span className="text-base sm:text-lg font-bold text-terracotta" data-testid={`plan-${plan.id}-fee`}>
                                ${thisPlanFee.toLocaleString()} MXN
                              </span>
                              <span className="text-[10px] text-gray-500 block sm:max-w-[170px] leading-tight">
                                {plan.rule}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-sm font-medium text-terracotta block">
                                {t("checkout.pctOrMin", { pct: plan.feePercent, min: plan.minFee })}
                              </span>
                              <span className="text-[10px] text-gray-500 block sm:max-w-[170px] leading-tight">
                                {plan.rule}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Description + benefits */}
                        <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
                        <ul className="mt-2 grid grid-cols-1 gap-y-1">
                          {plan.benefits.map((b, i) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span className="leading-snug">{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Locked Premium → VIP CTA */}
                    {isLocked && (
                      <div className="mt-3 pt-3 border-t border-amber-200" data-testid="premium-locked-cta">
                        <p className="text-xs text-amber-900 leading-relaxed">
                          {premiumEligibility.reason ||
                            t("checkout.premiumLockedDefault", { count: premiumEligibility.required_rentals || 10 })}
                        </p>
                        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <p className="font-semibold text-sm text-agave">{t("checkout.vipAnnual")}</p>
                            <p className="text-xs text-neutral-600">
                              {t("checkout.vipPerYear", { price: (premiumEligibility.vip_price_mxn || 3500).toLocaleString() })}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivateVip();
                            }}
                            disabled={vipCheckoutLoading}
                            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors"
                            data-testid="activate-vip-btn"
                          >
                            {vipCheckoutLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              t("checkout.activateVip")
                            )}
                          </button>
                        </div>

                        {/* Referral code input — optional. If valid, both parties earn 30 free days. */}
                        <div
                          className="mt-3 p-2 rounded-lg border border-amber-300/70 bg-white/60"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="vip-referral-block"
                        >
                          <label className="block text-[11px] font-medium text-amber-900 mb-1">
                            {t("checkout.vipReferralLabel")}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={vipReferralCode}
                              onChange={(e) =>
                                setVipReferralCode(e.target.value.toUpperCase())
                              }
                              placeholder="RVNS-XXXX"
                              className="flex-1 min-w-0 text-sm font-mono tracking-wider uppercase px-3 py-1.5 border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 placeholder:text-amber-300"
                              maxLength={9}
                              data-testid="vip-referral-input"
                            />
                            {vipReferralChecking && (
                              <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                            )}
                            {!vipReferralChecking && vipReferralStatus?.valid && (
                              <Check className="w-5 h-5 text-emerald-600" data-testid="vip-referral-valid" />
                            )}
                            {!vipReferralChecking && vipReferralStatus && !vipReferralStatus.valid && (
                              <span className="text-red-500 text-xs" data-testid="vip-referral-invalid">×</span>
                            )}
                          </div>
                          {vipReferralStatus?.valid && (
                            <p className="text-[11px] text-emerald-700 mt-1.5 font-medium">
                              {t("checkout.vipValidCode", { name: vipReferralStatus.referrer_name, days: vipReferralStatus.bonus_days })}
                            </p>
                          )}
                          {vipReferralStatus && !vipReferralStatus.valid && vipReferralCode.length >= 8 && (
                            <p className="text-[11px] text-red-600 mt-1.5">
                              {vipReferralStatus.reason === "not_found"
                                ? t("checkout.refNotFound")
                                : vipReferralStatus.reason === "self_referral"
                                ? t("checkout.refSelf")
                                : vipReferralStatus.reason === "already_used"
                                ? t("checkout.refUsed")
                                : t("checkout.refInvalid")}
                            </p>
                          )}
                        </div>
                        {(premiumEligibility.remaining_rentals ?? 10) > 0 && (
                          <p className="text-[11px] text-amber-700/80 mt-2">
                            {t("checkout.completeMoreRentals", { count: premiumEligibility.remaining_rentals })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 mt-4" data-testid="protection-rule-disclaimer">
                {t("booking.protectionRuleDisclaimer", "Se cobrará la cantidad mayor entre la tarifa mínima de protección y el porcentaje correspondiente del subtotal.")}
              </p>

              {/* Legal disclosures (Plan Premium gating + assistance-not-insurance) */}
              <div className="mt-4 space-y-2 text-[11px] text-neutral-500 leading-relaxed" data-testid="protection-legal-disclosures">
                <p>{t("checkout.legal1")}</p>
                <p>{t("checkout.legal2")}</p>
                <p>{t("checkout.legal3")}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Price Summary */}
          <div className="lg:col-span-1">
            <div className="card-base sticky top-24">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-terracotta" />
                {t("checkout.priceSummary")}
              </h3>

              {calculatingPrice ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
                </div>
              ) : priceBreakdown ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {t("checkout.daysLine", { rate: vehicle.daily_rate?.toLocaleString(), count: priceBreakdown.days })}
                    </span>
                    <span>${priceBreakdown.base_price?.toLocaleString()}</span>
                  </div>

                  {priceBreakdown.delivery_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t("checkout.delivery")}</span>
                      <span>${priceBreakdown.delivery_fee?.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {t("booking.protectionPlan")} (
                      {protectionPlan === "premier"
                        ? t("checkout.planPremium")
                        : protectionPlan === "standard"
                        ? t("checkout.planStandard")
                        : t("checkout.planBasic")}
                      )
                    </span>
                    <span>${priceBreakdown.protection_fee?.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t("booking.platformFee")}</span>
                    <span>${priceBreakdown.platform_fee?.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t("booking.tax")}</span>
                    <span>${priceBreakdown.tax?.toLocaleString()}</span>
                  </div>

                  <Separator className="my-4" />

                  {/* Rental subtotal (without deposit) */}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t("checkout.subtotalRental")}</span>
                    <span>${priceBreakdown.total_price?.toLocaleString()} MXN</span>
                  </div>

                  {/* Refundable deposit — NOW included in today's charge */}
                  {priceBreakdown.deposit_amount > 0 && (
                    <div
                      className="flex justify-between text-sm"
                      data-testid="checkout-deposit-row"
                    >
                      <span className="text-gray-600 flex items-center gap-1.5">
                        {t("checkout.refundableDeposit")}
                        <button
                          type="button"
                          onClick={() => setDepositOpen(true)}
                          className="text-amber-700/70 hover:text-amber-900 transition-colors inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                          aria-label={t("checkout.depositQ")}
                          data-testid="deposit-info-btn"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                      </span>
                      <span>${priceBreakdown.deposit_amount?.toLocaleString()} MXN</span>
                    </div>
                  )}

                  <Separator className="my-4" />

                  <div className="flex justify-between font-semibold text-lg">
                    <span>{t("checkout.totalDueToday")}</span>
                    <span className="text-terracotta" data-testid="checkout-total-due">
                      ${(priceBreakdown.total_due_today ?? priceBreakdown.total_price)?.toLocaleString()} MXN{" "}
                      <UsdHint amountMxn={priceBreakdown.total_due_today ?? priceBreakdown.total_price} />
                    </span>
                  </div>

                  {/* Optional recovery-code input — single-use abandoned-
                      checkout discount from the "Tu reserva te espera"
                      email. Hidden behind a tiny disclosure to keep the
                      summary clean for guests who don't have one. */}
                  <details className="mt-3 text-sm" data-testid="checkout-coupon-disclosure">
                    <summary className="cursor-pointer text-neutral-600 hover:text-neutral-800 text-xs select-none">
                      {t("checkout.couponQ")}
                    </summary>
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCapitalize="characters"
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().slice(0, 32))}
                        placeholder="VUELVE10-XXXXXX"
                        className="flex-1 input-base h-9 text-sm font-mono tracking-wider"
                        data-testid="checkout-coupon-input"
                      />
                    </div>
                    <p className="text-[11px] text-neutral-400 mt-1.5">
                      {t("checkout.couponHint")}
                    </p>
                  </details>

                  {/* Refundable deposit info card with 72h release rule */}
                  {priceBreakdown.deposit_amount > 0 && (
                    <div
                      className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/60"
                      data-testid="checkout-deposit-block"
                    >
                      <p className="text-xs text-amber-900 leading-relaxed">
                        <span className="font-semibold">{t("checkout.depositBlockA", { amount: priceBreakdown.deposit_amount?.toLocaleString() })}</span>{" "}
                        {t("checkout.depositBlockB")}{" "}
                        <span className="font-semibold">{t("checkout.depositBlockC")}</span>{" "}
                        {t("checkout.depositBlockD")}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  {t("checkout.selectDatesForPrice")}
                </p>
              )}

              {/* Contextual cancellation snippet */}
              {pickupDate && (() => {
                const hoursUntilPickup =
                  (pickupDate.getTime() - Date.now()) / (1000 * 60 * 60);
                const isFarBooking = hoursUntilPickup > 24;
                return (
                  <div
                    className="mt-5 p-4 rounded-xl border border-emerald-200 bg-emerald-50/60"
                    data-testid="checkout-cancel-snippet"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="inline-flex w-9 h-9 rounded-lg bg-emerald-100 items-center justify-center shrink-0"
                        aria-hidden
                      >
                        <Shield className="w-4 h-4 text-emerald-700" strokeWidth={2.2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-emerald-900 leading-snug">
                          {isFarBooking
                            ? t("carDetails.cancelFree24Title")
                            : t("carDetails.cancelFree1hTitle")}
                        </p>
                        <p className="text-xs text-emerald-800/80 mt-1 leading-relaxed">
                          {isFarBooking
                            ? t("carDetails.cancelFree24Body")
                            : t("checkout.cancelFree1hBodyNow")}
                        </p>
                        <Link
                          to="/cancelacion"
                          target="_blank"
                          rel="noopener"
                          data-testid="checkout-cancel-policy-link"
                          className="inline-block text-xs font-semibold text-emerald-700 hover:text-emerald-800 mt-1.5 hover:underline"
                        >
                          {t("carDetails.viewFullPolicy")}
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div
                className="mt-4 flex items-start gap-3 p-4 rounded-xl border border-neutral-200 bg-neutral-50"
                data-testid="checkout-terms-block"
              >
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(v === true)}
                  data-testid="checkout-terms-checkbox"
                  className="mt-0.5"
                />
                <Label
                  htmlFor="accept-terms"
                  className="text-sm font-normal text-neutral-700 leading-snug cursor-pointer"
                >
                  {t("checkout.acceptPrefix")}{" "}
                  <Link
                    to="/terminos"
                    target="_blank"
                    rel="noopener"
                    className="text-terracotta font-semibold hover:underline"
                    data-testid="checkout-terms-link"
                  >
                    {t("auth.termsTermsLink")}
                  </Link>
                  {t("auth.termsCommaThe")}{" "}
                  <Link
                    to="/elegibilidad"
                    target="_blank"
                    rel="noopener"
                    className="text-terracotta font-semibold hover:underline"
                    data-testid="checkout-eligibility-link"
                  >
                    {t("auth.termsEligibilityLink")}
                  </Link>{" "}
                  {t("auth.termsAndThe")}{" "}
                  <Link
                    to="/privacidad"
                    target="_blank"
                    rel="noopener"
                    className="text-terracotta font-semibold hover:underline"
                    data-testid="checkout-privacy-link"
                  >
                    {t("auth.termsPrivacyLink")}
                  </Link>{" "}
                  {t("auth.termsOfRivones")}
                </Label>
              </div>

              {/* Inline warning if the vehicle's host hasn't completed
                  Stripe Connect yet. Guests see this BEFORE clicking. */}
              {checkoutGateBlocked && (
                <div
                  className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-red-200 bg-red-50"
                  data-testid="checkout-host-not-ready"
                  role="alert"
                >
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 leading-snug">
                    {t("checkout.hostNotReady")}
                  </p>
                </div>
              )}

              <Button
                onClick={handleSubmit}
                disabled={submitting || !priceBreakdown || !acceptedTerms || checkoutGateBlocked}
                className="w-full btn-primary h-12 mt-4"
                data-testid="checkout-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("booking.processing")}
                  </>
                ) : (
                  t("booking.proceedToPayment")
                )}
              </Button>

              <p className="text-center text-xs text-gray-500 mt-4">
                {t("checkout.stripeRedirect")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* "Why a minimum fee?" trust-signal dialog */}
      <Dialog open={whyMinFeeOpen} onOpenChange={setWhyMinFeeOpen}>
        <DialogContent className="max-w-md" data-testid="why-min-fee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-terracotta" />
              {t("booking.whyMinFee.title", "¿Por qué hay una tarifa mínima?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "booking.whyMinFee.intro",
                "Cada viaje — sin importar la duración — activa los mismos servicios operativos. Por eso cobramos lo mayor entre el porcentaje y la tarifa mínima."
              )}
            </DialogDescription>
          </DialogHeader>
          <ul className="mt-2 space-y-3 text-sm text-neutral-700">
            <li className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.whyMinFee.bullet1Title", "Verificación de conductor")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.whyMinFee.bullet1Body",
                    "Validamos tu identidad con Stripe Identity y revisamos tu licencia antes de cada viaje."
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Headphones className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.whyMinFee.bullet2Title", "Asistencia 24/7 en carretera")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.whyMinFee.bullet2Body",
                    "Soporte por chat, llantazo o grúa durante toda tu reserva, sea de 1 hora o 10 días."
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ReceiptText className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.whyMinFee.bullet3Title", "Procesamiento y respaldo de daños")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.whyMinFee.bullet3Body",
                    "Cobertura para reclamos por daños y respaldo legal — un costo fijo que no escala con el precio del auto."
                  )}
                </p>
              </div>
            </li>
          </ul>
          <p className="mt-3 text-xs text-neutral-500 italic">
            {t(
              "booking.whyMinFee.closer",
              "En viajes largos, el porcentaje cubre estos costos por sí solo. En viajes cortos, la tarifa mínima los mantiene viables — para ti y para tu anfitrión."
            )}
          </p>
          <button
            onClick={() => setWhyMinFeeOpen(false)}
            className="mt-4 w-full btn-primary"
            data-testid="why-min-fee-close-btn"
          >
            {t("common.gotIt", "Entendido")}
          </button>
        </DialogContent>
      </Dialog>

      {/* "What is a security hold?" deposit dialog */}
      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent className="max-w-md" data-testid="deposit-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              {t("booking.deposit.title", "¿Qué es la retención de seguridad?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "booking.deposit.intro",
                "Es una autorización temporal en tu tarjeta — no un cargo. Funciona igual que el hold de un hotel o el de Airbnb."
              )}
            </DialogDescription>
          </DialogHeader>
          <ul className="mt-2 space-y-3 text-sm text-neutral-700">
            <li className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.deposit.bullet1Title", "Nunca se cobra a menos que haya daños")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.deposit.bullet1Body",
                    "Solo aplica si el anfitrión documenta un daño dentro del deducible de tu plan. Sin reporte = sin cargo."
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.deposit.bullet2Title", "Liberada en 5 días hábiles tras devolver el auto")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.deposit.bullet2Body",
                    "Una vez que el viaje termina sin incidentes, tu banco quita la retención automáticamente."
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CardIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-agave">
                  {t("booking.deposit.bullet3Title", "Sin intereses, sin impacto en tu crédito")}
                </p>
                <p className="text-neutral-600">
                  {t(
                    "booking.deposit.bullet3Body",
                    "La retención no genera intereses ni aparece como deuda. Solo reduce temporalmente tu límite disponible."
                  )}
                </p>
              </div>
            </li>
          </ul>
          <p className="mt-3 text-xs text-neutral-500 italic">
            {t(
              "booking.deposit.closer",
              "Asegúrate de tener este monto disponible en tu tarjeta al momento de pagar — pero recuerda, es una retención, no un cobro."
            )}
          </p>
          <button
            onClick={() => setDepositOpen(false)}
            className="mt-4 w-full btn-primary"
            data-testid="deposit-close-btn"
          >
            {t("common.gotIt", "Entendido")}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
